import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  stateHasSubpartK,
  stateHasSubpartP,
} from "encamp-shared/src/waste/rcraSectionAvailability";
import {
  RCRA_EXEMPT_BOILER_ACTIVITY_LABELS,
  RCRA_LAND_TYPE_LABELS,
  RCRA_RECOGNIZED_TRADER_LABELS,
  RCRA_RECYCLER_ACTIVITY_LABELS,
  RCRA_SLAB_LABELS,
  RCRA_SUBPART_K_ENTITY_TYPE_LABELS,
  RCRA_SUBPART_P_ACTIVITY_LABELS,
  RCRA_TRANSPORTER_ACTIVITY_LABELS,
  RCRA_UNIVERSAL_WASTE_TYPE_LABELS,
  RCRA_USED_OIL_MARKETER_LABELS,
  RCRA_USED_OIL_PROCESSOR_LABELS,
  RCRA_USED_OIL_TRANSPORTER_LABELS,
  formatBoolean,
  formatList,
} from "routes/Customer/Facility/ProfileV2/rcraProfileLabels";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";

import {
  Resolved870012Content,
  formatResolvedMailingAddress,
  formatResolvedSiteLocation,
} from "./frozenSnapshot";

/**
 * Read-only render of the filed Facility Profile, sourced from the 8700-12
 * snapshot. Deliberately mirrors the live `RcraProfileSection` field-for-field —
 * same "♻️ RCRA Profile" header, same labels, same formatters — so the frozen
 * view reads identically to the editable one (and includes every RCRA profile
 * activity field, not just site identification). Compliance-issue indicators are
 * omitted: a frozen submission's content is final.
 */
export function FrozenFacilityProfilePanel({ resolved }: { resolved: Resolved870012Content }) {
  const rcraProfile = resolved.rcraProfile ?? {};
  const epaIds = (resolved.epaIds ?? []).map((epa) => epa.epaId).filter((id): id is string => !!id);
  const otherNaicsCodes = resolved.naics?.otherNaicsCodes ?? [];
  const siteLocationText = formatResolvedSiteLocation(resolved.siteLocation);
  const mailingAddressText = formatResolvedMailingAddress(resolved.siteMailingAddress);

  // Mirror the live section: Subpart K/P are only collected on some states'
  // 8700-12 forms, so hide them for filings whose site state doesn't offer them.
  const showSubpartK = stateHasSubpartK(resolved.siteLocation?.state);
  const showSubpartP = stateHasSubpartP(resolved.siteLocation?.state);

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        ♻️ RCRA Profile
      </Typography>

      {/* EPA ID(s) head the profile, matching the editable Facility Profile step
          (they are assigned by EPA and read-only there too). */}
      <Box mb={2}>
        <DisplayOnlyField
          label={epaIds.length > 1 ? "EPA IDs" : "EPA ID"}
          text={epaIds.join(", ")}
        />
      </Box>

      <Box mb={2}>
        <DisplayOnlyField label="RCRA Site Name" text={resolved.siteName?.siteName ?? ""} />
      </Box>

      <Grid container columnSpacing={3} rowSpacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Site Location"
            text={siteLocationText}
            renderValue={() => (
              <CopyableTypography
                text={siteLocationText}
                sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
                tooltipOptions={{ disableHoverMessage: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Site Mailing Address"
            text={mailingAddressText}
            renderValue={() => (
              <CopyableTypography
                text={mailingAddressText}
                sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
                tooltipOptions={{ disableHoverMessage: true }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Land Type"
            text={resolved.landType ? RCRA_LAND_TYPE_LABELS[resolved.landType] : ""}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Primary NAICS Code"
            text={resolved.naics?.primaryNaicsCode?.toString() ?? ""}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Other NAICS Codes"
            renderValue={() =>
              otherNaicsCodes.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {otherNaicsCodes.map((code) => (
                    <Chip key={code} label={code.toString()} size="small" />
                  ))}
                </Stack>
              ) : (
                ""
              )
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Short-Term Generator"
            text={formatBoolean(rcraProfile.shortTermGenerator)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
          <DisplayOnlyField
            label="Short-Term Generator Notes"
            text={rcraProfile.shortTermGeneratorNotes ?? ""}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Treater, Storer, or Disposer of Haz Waste (TSD)"
            text={formatBoolean(rcraProfile.tsdActivity)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Receives Hazardous Waste from Off-site"
            text={formatBoolean(rcraProfile.offSiteReceipt)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Recycler Activity"
            text={
              rcraProfile.recyclerActivity
                ? RCRA_RECYCLER_ACTIVITY_LABELS[rcraProfile.recyclerActivity]
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
          <DisplayOnlyField
            label="Exempt Boiler / Industrial Furnace"
            text={formatList(
              rcraProfile.exemptBoilerActivities,
              RCRA_EXEMPT_BOILER_ACTIVITY_LABELS
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Transporter Activity"
            text={
              rcraProfile.transporterActivity
                ? RCRA_TRANSPORTER_ACTIVITY_LABELS[rcraProfile.transporterActivity]
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Underground Injection Control"
            text={formatBoolean(rcraProfile.undergroundInjectionControl)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="US Importer of Hazardous Waste"
            text={formatBoolean(rcraProfile.usImporterOfHazardousWaste)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Recognized Trader Activity"
            text={
              rcraProfile.recognizedTraderActivity
                ? RCRA_RECOGNIZED_TRADER_LABELS[rcraProfile.recognizedTraderActivity]
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="SLAB Importer / Exporter"
            text={rcraProfile.slabImporter ? RCRA_SLAB_LABELS[rcraProfile.slabImporter] : ""}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
          <DisplayOnlyField
            label="Universal Waste Types (LQHUW)"
            text={formatList(rcraProfile.lqhuwActivities, RCRA_UNIVERSAL_WASTE_TYPE_LABELS)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Universal Waste Destination Facility"
            text={formatBoolean(rcraProfile.universalWasteDestFacility)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Used Oil Transporter Activities"
            text={formatList(
              rcraProfile.usedOilTransporterActivities,
              RCRA_USED_OIL_TRANSPORTER_LABELS
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Used Oil Processor Activities"
            text={formatList(
              rcraProfile.usedOilProcessorActivities,
              RCRA_USED_OIL_PROCESSOR_LABELS
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Off-Specification Used Oil Burner"
            text={formatBoolean(rcraProfile.offSpecUsedOilBurner)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
          <DisplayOnlyField
            label="Used Oil Marketer Activities"
            text={formatList(rcraProfile.usedOilMarketerActivities, RCRA_USED_OIL_MARKETER_LABELS)}
          />
        </Grid>

        {showSubpartP && (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Subpart P Activity (Pharmaceutical)"
                text={
                  rcraProfile.subpartPActivity
                    ? RCRA_SUBPART_P_ACTIVITY_LABELS[rcraProfile.subpartPActivity]
                    : ""
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Subpart P Withdrawal"
                text={formatBoolean(rcraProfile.subpartPWithdrawal)}
              />
            </Grid>
          </>
        )}
        {showSubpartK && (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Subpart K Withdrawal"
                text={formatBoolean(rcraProfile.subpartKWithdrawal)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
              <DisplayOnlyField
                label="Subpart K Entity Types (Academic Lab)"
                text={formatList(
                  rcraProfile.subpartKEntityTypes,
                  RCRA_SUBPART_K_ENTITY_TYPE_LABELS
                )}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Electronic Manifest Broker"
            text={formatBoolean(rcraProfile.manifestBroker)}
          />
        </Grid>
        {/* §1 Reason for Submittal and §18 Public Comments are submission-level and
            shown in the overview Details panel, not in this facility profile section. */}
      </Grid>
    </Box>
  );
}
